import React, { useState, useEffect } from 'react';
import { X, Save, Package, Tag, DollarSign, Layers, Image as ImageIcon, AlertCircle, Hash, ToggleLeft, ToggleRight, Plus, Scale, Upload, Trash2, Loader2 } from 'lucide-react';
import { getProductCategories, getProductUnits, getPresignedUploadUrl, uploadImageToPresignedUrl } from '../../../../services/api/productApi';

const ProductEditModal = ({ product, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        itemCode: product?.itemCode || '',
        name: product?.name || '',
        category: product?.category || 'Produce',
        price: product?.price || 0,
        retailPrice: product?.retailPrice || 0,
        discountedPrice: product?.discountedPrice || '',
        discountedRetailPrice: product?.discountedRetailPrice || '',
        stock: product?.stock || 0,
        unit: product?.unit || '',
        image: product?.image || '',
        active: product?.active !== false,
        vatRate: product?.vatRate !== undefined ? product.vatRate : 5,
        priceIncludesVat: product?.priceIncludesVat !== undefined ? product.priceIncludesVat : false
    });

    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);

    const handleImageFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadingImage(true);
        setErrors(prev => ({ ...prev, image: '' }));

        try {
            const uploadedUrl = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = (event) => {
                    const img = new Image();
                    img.src = event.target.result;
                    img.onload = async () => {
                        try {
                            const size = Math.min(img.width, img.height);
                            const canvas = document.createElement('canvas');
                            canvas.width = 600;
                            canvas.height = 600;
                            const ctx = canvas.getContext('2d');

                            const sx = (img.width - size) / 2;
                            const sy = (img.height - size) / 2;
                            ctx.drawImage(img, sx, sy, size, size, 0, 0, 600, 600);

                            canvas.toBlob(async (blob) => {
                                try {
                                    if (!blob) {
                                        reject(new Error("Canvas blob generation failed"));
                                        return;
                                    }
                                    const cleanFilename = file.name.replace(/\.[^/.]+$/, "") + ".webp";
                                    const { uploadUrl, publicUrl } = await getPresignedUploadUrl(cleanFilename, "image/webp");
                                    await uploadImageToPresignedUrl(uploadUrl, blob);
                                    resolve(publicUrl);
                                } catch (err) {
                                    reject(err);
                                }
                            }, 'image/webp', 0.85);
                        } catch (err) {
                            reject(err);
                        }
                    };
                    img.onerror = () => reject(new Error("Failed to load image file"));
                };
                reader.onerror = () => reject(new Error("Failed to read image file"));
            });

            handleChange('image', uploadedUrl);
        } catch (err) {
            console.error("Failed to upload image:", err);
            setErrors(prev => ({ ...prev, image: 'Failed to upload image. Please try again.' }));
        } finally {
            setUploadingImage(false);
        }
    };

    const [categories, setCategories] = useState(['Produce', 'Grains', 'Oil', 'Meat', 'Spices', 'Dairy', 'Beverages', 'Snacks']);
    const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [categoryError, setCategoryError] = useState('');

    const [availableUnits, setAvailableUnits] = useState(() => {
        const defaults = ['kg', 'pcs', 'box', 'pkt', 'liter', 'gram', 'dozen', 'set', 'bag', 'carton'];
        if (product?.unit && !defaults.includes(product.unit)) {
            return [...defaults, product.unit];
        }
        return defaults;
    });
    const [isAddingNewUnit, setIsAddingNewUnit] = useState(false);
    const [newUnitName, setNewUnitName] = useState('');
    const [unitError, setUnitError] = useState('');

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const cats = await getProductCategories();
                if (cats && cats.length > 0) {
                    setCategories(prev => [...new Set([...prev, ...cats])]);
                }
            } catch (err) {
                console.error("Failed to load categories:", err);
            }
        };
        const fetchUnits = async () => {
            try {
                const units = await getProductUnits();
                if (units && units.length > 0) {
                    setAvailableUnits(prev => [...new Set([...prev, ...units])]);
                }
            } catch (err) {
                console.error("Failed to load units:", err);
            }
        };
        fetchCategories();
        fetchUnits();
    }, []);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.itemCode.trim()) newErrors.itemCode = 'Required';
        if (!formData.name.trim()) newErrors.name = 'Required';
        // Prices are now optional
        if (formData.stock !== '' && formData.stock < 0) newErrors.stock = 'Cannot be negative';
        
        const unitToValidate = isAddingNewUnit ? newUnitName.trim() : formData.unit;
        if (!unitToValidate) newErrors.unit = 'Required';
        
        // Validate discounted prices if provided
        if (formData.discountedPrice && parseFloat(formData.discountedPrice) >= parseFloat(formData.price)) {
            newErrors.discountedPrice = 'Must be < regular price';
        }
        if (formData.discountedRetailPrice && parseFloat(formData.discountedRetailPrice) >= parseFloat(formData.retailPrice)) {
            newErrors.discountedRetailPrice = 'Must be < regular price';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsSaving(true);
        try {
            // Automatically capture typed newUnitName if isAddingNewUnit is active
            let finalUnit = formData.unit;
            if (isAddingNewUnit && newUnitName.trim()) {
                finalUnit = newUnitName.trim();
            }

            await onSave({
                ...product,
                ...formData,
                unit: finalUnit,
                price: formData.price === '' ? 0 : parseFloat(formData.price),
                retailPrice: formData.retailPrice === '' ? 0 : parseFloat(formData.retailPrice),
                discountedPrice: formData.discountedPrice ? parseFloat(formData.discountedPrice) : null,
                discountedRetailPrice: formData.discountedRetailPrice ? parseFloat(formData.discountedRetailPrice) : null,
                stock: formData.stock === '' ? 0 : parseInt(formData.stock),
                vatRate: formData.vatRate === '' ? 5 : parseFloat(formData.vatRate),
                priceIncludesVat: !!formData.priceIncludesVat
            });
            onClose();
        } catch (err) {
            console.error("Failed to save product:", err);
            setErrors(prev => ({ ...prev, submit: err.message || 'Failed to save product' }));
        } finally {
            setIsSaving(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const profitMargin = formData.price > 0 && formData.retailPrice > 0
        ? ((formData.retailPrice - formData.price) / formData.price * 100).toFixed(1)
        : 0;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-3 animate-fade-in">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl sm:w-[85%] md:w-[75%] lg:w-[70%] max-h-[calc(100dvh-2rem)] overflow-hidden animate-scale-in flex flex-col">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-5 py-3 relative flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="absolute top-2 right-2 p-1.5 hover:bg-white/20 rounded-full transition-all"
                        disabled={isSaving}
                    >
                        <X size={18} />
                    </button>
                    <h2 className="text-lg font-bold pr-8">{product?.id ? 'Edit Product' : 'Add New Product'}</h2>
                    <p className="text-blue-100 text-xs mt-0.5">{product?.id ? 'Update information and pricing' : 'Enter product details'}</p>
                </div>

                {/* Form Content */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                    {errors.submit && (
                        <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-xs font-semibold">
                            <AlertCircle size={14} className="flex-shrink-0" />
                            <span>{errors.submit}</span>
                        </div>
                    )}
                    <div className="grid grid-cols-12 gap-3">
                        {/* Left Column */}
                        <div className="col-span-12 md:col-span-5 space-y-2.5">
                            {/* Item Code */}
                            <div>
                                <label className="text-xs font-semibold text-gray-600 flex items-center gap-1 mb-1">
                                    <Hash size={12} />
                                    Item Code <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.itemCode}
                                    onChange={(e) => handleChange('itemCode', e.target.value)}
                                    disabled={!!product?.id}
                                    className={`w-full px-2.5 py-1.5 text-xs border-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 ${errors.itemCode ? 'border-red-300 bg-red-50' : 'border-gray-300'} ${product?.id ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    placeholder="e.g., PROD-001"
                                />
                                {errors.itemCode && <p className="text-xs text-red-600 mt-0.5">{errors.itemCode}</p>}
                                {product?.id && <p className="text-xs text-gray-500 mt-0.5">Item code cannot be changed</p>}
                            </div>

                            {/* Product Name */}
                            <div>
                                <label className="text-xs font-semibold text-gray-600 flex items-center gap-1 mb-1">
                                    <Package size={12} />
                                    Product Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    className={`w-full px-2.5 py-1.5 text-xs border-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 ${errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                />
                                {errors.name && <p className="text-xs text-red-600 mt-0.5">{errors.name}</p>}
                            </div>

                            {/* Category */}
                            <div>
                                <div className="flex justify-between items-center mb-1">
                                    <label className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                                        <Tag size={12} />
                                        Category <span className="text-gray-400 text-xs">(UI only)</span>
                                    </label>
                                    <button 
                                        type="button"
                                        onClick={() => setIsAddingNewCategory(!isAddingNewCategory)}
                                        className="text-[10px] text-blue-600 hover:underline font-bold flex items-center gap-0.5"
                                    >
                                        {isAddingNewCategory ? 'Cancel' : <><Plus size={10} /> New Category</>}
                                    </button>
                                </div>
                                
                                {isAddingNewCategory ? (
                                    <>
                                    <div className="flex gap-1 animate-fade-in">
                                        <input
                                            type="text"
                                            value={newCategoryName}
                                            onChange={(e) => {
                                                setNewCategoryName(e.target.value);
                                                setCategoryError('');
                                            }}
                                            placeholder="Enter category..."
                                            className={`flex-1 px-2.5 py-1.5 text-xs border-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 ${categoryError ? 'border-red-400 bg-red-50' : 'border-blue-400'}`}
                                            autoFocus
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (newCategoryName.trim()) {
                                                    const trimmed = newCategoryName.trim();
                                                    // Check for duplicate (case-insensitive)
                                                    const isDuplicate = categories.some(c => c.toLowerCase() === trimmed.toLowerCase());
                                                    if (isDuplicate) {
                                                        setCategoryError('Category already exists');
                                                        return;
                                                    }
                                                    setCategories(prev => [...prev, trimmed]);
                                                    handleChange('category', trimmed);
                                                    setNewCategoryName('');
                                                    setIsAddingNewCategory(false);
                                                    setCategoryError('');
                                                }
                                            }}
                                            className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-xs font-bold hover:bg-blue-600 transition-colors"
                                        >
                                            Add
                                        </button>
                                    </div>
                                    {categoryError && <p className="text-[10px] text-red-600 mt-1 font-semibold">{categoryError}</p>}
                                    </>
                                ) : (
                                    <select
                                        value={formData.category}
                                        onChange={(e) => handleChange('category', e.target.value)}
                                        className="w-full px-2.5 py-1.5 text-xs border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                                    >
                                        {categories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            {/* Unit & Stock */}
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <label className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                                            <Scale size={12} />
                                            Unit <span className="text-red-500">*</span>
                                        </label>
                                        <button 
                                            type="button"
                                            onClick={() => setIsAddingNewUnit(!isAddingNewUnit)}
                                            className="text-[10px] text-blue-600 hover:underline font-bold flex items-center gap-0.5"
                                        >
                                            {isAddingNewUnit ? 'Cancel' : <><Plus size={10} /> New</>}
                                        </button>
                                    </div>

                                    {isAddingNewUnit ? (
                                        <>
                                        <div className="flex gap-1 animate-fade-in">
                                            <input
                                                type="text"
                                                value={newUnitName}
                                                onChange={(e) => {
                                                    setNewUnitName(e.target.value);
                                                    setUnitError('');
                                                    if (errors.unit) setErrors(prev => ({ ...prev, unit: '' }));
                                                }}
                                                placeholder="e.g. box"
                                                className={`flex-1 min-w-0 px-2.5 py-1.5 text-xs border-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 ${unitError ? 'border-red-400 bg-red-50' : 'border-blue-400'}`}
                                                autoFocus
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (newUnitName.trim()) {
                                                        const trimmed = newUnitName.trim();
                                                        // Check for duplicate (case-insensitive)
                                                        const isDuplicate = availableUnits.some(u => u.toLowerCase() === trimmed.toLowerCase());
                                                        if (isDuplicate) {
                                                            setUnitError('Exists');
                                                            return;
                                                        }
                                                        setAvailableUnits(prev => [...prev, trimmed]);
                                                        handleChange('unit', trimmed);
                                                        setNewUnitName('');
                                                        setIsAddingNewUnit(false);
                                                        setUnitError('');
                                                    }
                                                }}
                                                className="px-2 py-1.5 bg-blue-500 text-white rounded-lg text-xs font-bold hover:bg-blue-600 transition-colors"
                                            >
                                                Add
                                            </button>
                                        </div>
                                        {unitError && <p className="text-[10px] text-red-600 mt-1 font-semibold">{unitError}</p>}
                                        {errors.unit && <p className="text-[10px] text-red-600 mt-1 font-semibold">{errors.unit}</p>}
                                        </>
                                    ) : (
                                        <select
                                            value={formData.unit}
                                            onChange={(e) => handleChange('unit', e.target.value)}
                                            className={`w-full px-2.5 py-1.5 text-xs border-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 ${errors.unit ? 'border-red-300 bg-red-50' : 'border-gray-300'} bg-white`}
                                        >
                                            <option value="">Select Unit</option>
                                            {availableUnits.map(u => (
                                                <option key={u} value={u}>{u}</option>
                                            ))}
                                        </select>
                                    )}
                                    {errors.unit && !isAddingNewUnit && <p className="text-xs text-red-600 mt-0.5">{errors.unit}</p>}
                                </div>

                                <div>
                                    <label className="text-xs font-semibold text-gray-600 flex items-center gap-1 mb-1">
                                        <Layers size={12} />
                                        Stock
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.stock}
                                        onChange={(e) => handleChange('stock', e.target.value)}
                                        className={`w-full px-2.5 py-1.5 text-xs border-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 ${errors.stock ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                                    />
                                    {errors.stock && <p className="text-xs text-red-600 mt-0.5">{errors.stock}</p>}
                                </div>
                            </div>

                            {/* Active Status */}
                            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                <label className="flex items-center justify-between cursor-pointer">
                                    <div className="flex items-center gap-2">
                                        {formData.active ? <ToggleRight size={18} className="text-green-600" /> : <ToggleLeft size={18} className="text-gray-400" />}
                                        <span className="text-xs font-semibold text-gray-700">Active Status</span>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={formData.active}
                                        onChange={(e) => handleChange('active', e.target.checked)}
                                        className="sr-only"
                                    />
                                    <div className={`w-10 h-5 rounded-full transition-colors ${formData.active ? 'bg-green-500' : 'bg-gray-300'}`}>
                                        <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${formData.active ? 'translate-x-5' : 'translate-x-0.5'} mt-0.5`}></div>
                                    </div>
                                </label>
                                <p className="text-xs text-gray-500 mt-1">{formData.active ? 'Product is visible and available' : 'Product is hidden from customers'}</p>
                            </div>
                        </div>

                        {/* Right Column - Pricing */}
                        <div className="col-span-12 md:col-span-7 space-y-2.5">
                            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-2xl border border-green-100">
                                <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-1.5">
                                    <DollarSign size={16} className="text-green-600" />
                                    Pricing
                                </h3>

                                {/* Wholesale Pricing */}
                                <div className="grid grid-cols-2 gap-3 mb-3">
                                    <div>
                                        <label className="text-xs font-semibold text-gray-600 mb-1 block">
                                            Wholesale Price <span className="text-gray-400">(optional)</span>
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-xs font-semibold">AED</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={formData.price}
                                                onChange={(e) => handleChange('price', e.target.value)}
                                                className={`w-full pl-12 pr-2.5 py-2 text-sm border-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 ${errors.price ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'}`}
                                            />
                                        </div>
                                        {errors.price && <p className="text-xs text-red-600 mt-0.5">{errors.price}</p>}
                                    </div>

                                    <div>
                                        <label className="text-xs font-semibold text-gray-600 mb-1 block">
                                            Discounted Wholesale <span className="text-gray-400">(optional)</span>
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-xs font-semibold">AED</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={formData.discountedPrice}
                                                onChange={(e) => handleChange('discountedPrice', e.target.value)}
                                                className={`w-full pl-12 pr-2.5 py-2 text-sm border-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 ${errors.discountedPrice ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'}`}
                                            />
                                        </div>
                                        {errors.discountedPrice && <p className="text-xs text-red-600 mt-0.5">{errors.discountedPrice}</p>}
                                    </div>
                                </div>

                                {/* Retail Pricing */}
                                <div className="grid grid-cols-2 gap-3 mb-3">
                                    <div>
                                        <label className="text-xs font-semibold text-gray-600 mb-1 block">
                                            Retail Price <span className="text-gray-400">(optional)</span>
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-xs font-semibold">AED</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={formData.retailPrice}
                                                onChange={(e) => handleChange('retailPrice', e.target.value)}
                                                className={`w-full pl-12 pr-2.5 py-2 text-sm border-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 ${errors.retailPrice ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'}`}
                                            />
                                        </div>
                                        {errors.retailPrice && <p className="text-xs text-red-600 mt-0.5">{errors.retailPrice}</p>}
                                    </div>

                                    <div>
                                        <label className="text-xs font-semibold text-gray-600 mb-1 block">
                                            Discounted Retail <span className="text-gray-400">(optional)</span>
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-xs font-semibold">AED</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={formData.discountedRetailPrice}
                                                onChange={(e) => handleChange('discountedRetailPrice', e.target.value)}
                                                className={`w-full pl-12 pr-2.5 py-2 text-sm border-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 ${errors.discountedRetailPrice ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'}`}
                                            />
                                        </div>
                                        {errors.discountedRetailPrice && <p className="text-xs text-red-600 mt-0.5">{errors.discountedRetailPrice}</p>}
                                    </div>
                                </div>

                                {/* Profit Metrics */}
                                {formData.price > 0 && formData.retailPrice > 0 && (
                                    <div className="grid grid-cols-3 gap-2 mt-3">
                                        <div className="bg-white p-2 rounded-lg border border-green-200 text-center">
                                            <div className="text-xs text-gray-600 mb-0.5">Margin</div>
                                            <div className={`text-sm font-bold ${profitMargin > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                {profitMargin}%
                                            </div>
                                        </div>
                                        <div className="bg-white p-2 rounded-lg border border-green-200 text-center">
                                            <div className="text-xs text-gray-600 mb-0.5">Profit/Unit</div>
                                            <div className="text-sm font-bold text-blue-600">
                                                {(formData.retailPrice - formData.price).toFixed(2)}
                                            </div>
                                        </div>
                                        <div className="bg-white p-2 rounded-lg border border-green-200 text-center">
                                            <div className="text-xs text-gray-600 mb-0.5">Stock Value</div>
                                            <div className="text-sm font-bold text-purple-600">
                                                {(formData.stock * formData.price).toFixed(0)}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* VAT Fields */}
                                <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-green-200">
                                    <div>
                                        <label className="text-xs font-semibold text-gray-600 mb-1 block">
                                            VAT Rate (%)
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={formData.vatRate}
                                            onChange={(e) => handleChange('vatRate', e.target.value)}
                                            className="w-full px-2.5 py-1.5 text-xs border-2 border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500"
                                            placeholder="5"
                                        />
                                    </div>
                                    <div className="flex flex-col justify-end">
                                        <label className="flex items-center gap-2 cursor-pointer pb-2">
                                            <input
                                                type="checkbox"
                                                checked={formData.priceIncludesVat}
                                                onChange={(e) => handleChange('priceIncludesVat', e.target.checked)}
                                                className="rounded border-gray-300 text-green-600 focus:ring-green-500 text-xs"
                                            />
                                            <span className="text-xs font-semibold text-gray-700">Price Includes VAT</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Image Upload & URL */}
                                <div className="mt-3 pt-3 border-t border-green-200">
                                    <label className="text-xs font-semibold text-gray-600 flex items-center gap-1 mb-2">
                                        <ImageIcon size={12} />
                                        Product Image
                                    </label>
                                    
                                    {formData.image ? (
                                        <div className="flex gap-4 items-center bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                                            <img 
                                                src={formData.image} 
                                                alt="Preview" 
                                                className="w-16 h-16 object-cover rounded-lg border border-gray-100 shadow-inner"
                                                onError={(e) => { e.target.src = 'https://placehold.co/150?text=Error'; }}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-semibold text-gray-700 truncate">{formData.image}</p>
                                                <button
                                                    type="button"
                                                    onClick={() => handleChange('image', '')}
                                                    className="mt-1 flex items-center gap-1 text-[10px] text-red-600 font-bold hover:underline"
                                                >
                                                    <Trash2 size={10} /> Remove Image
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-3">
                                            {/* File selector dropzone box */}
                                            <label className="border-2 border-dashed border-gray-300 rounded-xl p-4 flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50/50 transition-all text-center h-24">
                                                {uploadingImage ? (
                                                    <>
                                                        <Loader2 className="w-6 h-6 text-blue-500 animate-spin mb-1" />
                                                        <span className="text-[10px] font-bold text-blue-500">Uploading...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Upload className="w-6 h-6 text-gray-400 mb-1" />
                                                        <span className="text-[10px] font-bold text-gray-600">Select Image File</span>
                                                        <span className="text-[9px] text-gray-400 mt-0.5">Auto center-cropped to 1:1</span>
                                                    </>
                                                )}
                                                <input 
                                                    type="file" 
                                                    accept="image/*" 
                                                    className="hidden" 
                                                    onChange={handleImageFileChange}
                                                    disabled={uploadingImage}
                                                />
                                            </label>
                                            
                                            {/* Text URL Fallback */}
                                            <div className="flex flex-col justify-center">
                                                <span className="text-[10px] font-bold text-gray-500 mb-1">Or paste image URL:</span>
                                                <input
                                                    type="text"
                                                    value={formData.image}
                                                    onChange={(e) => handleChange('image', e.target.value)}
                                                    className="w-full px-2.5 py-1.5 text-xs border-2 border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                    placeholder="https://example.com/image.jpg"
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {errors.image && <p className="text-xs text-red-600 mt-1 font-semibold">{errors.image}</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                </form>

                {/* Footer */}
                <div className="border-t border-gray-200 px-4 py-2.5 bg-gray-50 flex gap-2 justify-end flex-shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSaving}
                        className="px-4 py-1.5 text-xs border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-all font-semibold disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSaving}
                        className="px-4 py-1.5 text-xs bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all font-semibold shadow-md hover:shadow-lg disabled:opacity-50 flex items-center gap-1.5"
                    >
                        {isSaving ? (
                            <>
                                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save size={14} />
                                {product?.id ? 'Save Changes' : 'Add Product'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProductEditModal;
